import PropTypes from "prop-types";
import React, { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Row, Col, Alert, Card, CardBody, Container, FormFeedback, Input, Label, Form } from "reactstrap";

import { Link } from "react-router-dom";
import withRouter from "../../Components/Common/withRouter";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

// API helper
import { APIClient } from "../../helpers/api_helper";

// import images
// Logo is now loaded from public folder
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

const ResetPasswordPage = (props: any) => {
  const { email, resetToken } = useParams<{ email: string; resetToken: string }>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const apiClient = new APIClient();

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: email ? decodeURIComponent(email) : '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .matches(PASSWORD_RULE, "Password must contain at least one uppercase letter, one lowercase letter, and one number, and must be a minimum of 8 characters long.")
        .required("Please enter your new password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), undefined], 'Passwords must match')
        .required("Please confirm your password")
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      try {
        const response: any = await apiClient.create('/user/resetpassword', {
          email: values.email,
          newPassword: values.newPassword,
          resetToken: resetToken
        });

        if (response.status === 200) {
          setSuccessMessage('Password reset successfully! You can now login with your new password.');
          setIsSuccess(true);
          validation.resetForm();
        } else {
          setError(response.message || 'Failed to reset password');
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Network error. Please try again.';
        setError(errorMessage);
        console.error('Reset password error:', err);
      } finally {
        setLoading(false);
      }
    }
  });

  // Redirect if email or resetToken is missing
  if (!email || !resetToken) {
    return <Navigate to="/login" replace />;
  }

  document.title = "Reset Password | Velzon - React Admin & Dashboard Template";

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div>
                  <Link to="/" className="d-inline-block auth-logo">
                    <div className="d-flex align-items-center gap-2 justify-content-center">
                      <i className="ri-sparkling-line" style={{ fontSize: '28px', color: '#7F13EC' }}></i>
                      <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#ffffff' }}>Integr.Dev</span>
                    </div>
                  </Link>
                </div>
                <p className="mt-3 fs-15 fw-medium">Premium Admin & Dashboard Template</p>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">Reset Password</h5>
                    <p className="text-muted">Enter your new password</p>
                    
                    <i className="ri-lock-unlock-line display-5 text-success mb-3"></i>
                  </div>

                  <div className="p-2">
                    {error && (
                      <Alert color="danger" style={{ marginTop: "13px" }}>
                        {error}
                      </Alert>
                    )}
                    {successMessage && (
                      <Alert color="success" style={{ marginTop: "13px" }}>
                        {successMessage}
                      </Alert>
                    )}

                    {!isSuccess ? (
                      <Form
                        onSubmit={(e: any) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Input
                            name="email"
                            className="form-control"
                            type="email"
                            value={validation.values.email}
                            disabled
                          />
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">New Password</Label>
                          <Input
                            name="newPassword"
                            className="form-control"
                            placeholder="Enter new password"
                            type="password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.newPassword || ""}
                            invalid={
                              validation.touched.newPassword && validation.errors.newPassword ? true : false
                            }
                          />
                          {validation.touched.newPassword && validation.errors.newPassword ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.newPassword}</div>
                            </FormFeedback>
                          ) : null}
                          <small className="text-muted">Password must contain at least one uppercase letter, one lowercase letter, one number, and be at least 8 characters long.</small>
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Confirm New Password</Label>
                          <Input
                            name="confirmPassword"
                            className="form-control"
                            placeholder="Confirm new password"
                            type="password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword || ""}
                            invalid={
                              validation.touched.confirmPassword && validation.errors.confirmPassword ? true : false
                            }
                          />
                          {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.confirmPassword}</div>
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="text-center mt-4">
                          <button 
                            className="btn btn-success w-100" 
                            type="submit"
                            disabled={loading}
                          >
                            {loading ? 'Resetting...' : 'Reset Password'}
                          </button>
                        </div>
                      </Form>
                    ) : (
                      <div className="text-center mt-4">
                        <Link to="/login" className="btn btn-primary w-100">
                          Go to Login
                        </Link>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              <div className="mt-4 text-center">
                <p className="mb-0">Remember your password? <Link to="/login" className="fw-semibold text-primary text-decoration-underline"> Click here to login </Link> </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

ResetPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ResetPasswordPage);